import {F, getIntlTranslation, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import React, {useEffect, useState} from 'react';
import {useDispatch, useIntl, useSelector} from 'umi';
import {Button, Flex, InputNumber, message, Modal, Segmented, Select, Space, Switch, Typography} from 'antd';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import {ProForm, ProFormItem, ProFormText} from '@ant-design/pro-form';
import isEmpty from 'lodash/isEmpty';
import * as LocationSelectors from '@/selectors/locationSelectors';

import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import CopyableId from '@/components/CopyableId';
import DrawerSection from '@/components/Drawer/DrawerSection';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import ExtraFieldSelector from '@/components/ExtraFields/ExtraFieldSelector';
import {ExtraFieldTypeModel} from '@/typings/models/ExtraFields';
import {
  createServiceTypeRequest,
  deleteServiceTypeThumbnailRequest,
  editServiceTypeRequest,
  guessServiceTypeIconRequest,
  updateAllServicesForServiceTypeRequest,
} from '@/services/api/location';
import {CheckAccess} from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import UserOpenHours from '@/components/UserDrawer/Edit/UserOpenHours';
import {transformServiceTypeOpenHoursToSlots, transformSlotsToServiceTypeOpenHours} from '@/utils/openHours';
import {SERVICE_PROCESSING_WORKFLOW_TYPE} from '@/components/LocationDrawers/ServiceTypesDrawer/constants';
import styles from './index.less';
import EmojiSelectorFormItem from '@/components/Form/EmojiSelectorFormItem';
import {useAlertBeforeUnload} from '@/utils/hooks';
import {processAPIResponseNow} from '@/utils/request';
import ProductGroupSelect from '@/components/Filters/ProductGroupSelect';
import {URLParamBuilder} from '@/utils/functions';
import Paths from '@/services/paths';
import {API_PATH_PREFIX} from '@/constants/path';
import ThumbnailUpload from '@/components/ThumbnailUpload';
import {getRootUrl} from '@/constants/environment';
import ServiceBookingSettings from '../BranchDrawer/ServiceBookingSettings';
import ToggleCard from '@/components/ToggleCard/ToggleCard';

const {Paragraph} = Typography;

const ServiceTypesDrawer: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const fetchLoading = useSelector((state: DefaultRootState) => state.loading?.effects['location/fetchServiceTypes']);
  const drawer = useSelector(LocationSelectors.selectServiceTypeDrawer);
  const [initialized, setInitialized] = useState<boolean>(false)
  const timezones = useSelector(LocationSelectors.selectTimezones);
  const visible = !isEmpty(omit(drawer, ['toggledAt']));
  const dispatch = useDispatch();
  const [serviceTypeForm] = ProForm.useForm();

  const [editDirty, setEditDirty] = useState(false);

  // `overbookingEnabled` is UI-only state (a collapsible), intentionally NOT sent
  // in the payload. The backend only accepts `unlimited_booking` / `additional_capacity`,
  // and rejects (422) any combination where `additional_capacity` is sent together with
  // `unlimited_booking: true` or `ignore_agent_availability: true`. So we keep the toggle
  // local and derive the real payload from it via `buildOverbookingPayload`.
  const [overbookingEnabled, setOverbookingEnabled] = useState(false);
  // `limitEnabled` is the UI "Limit" toggle: off => unlimited overbooking
  // (`unlimited_booking: true`), on (with a number) => a finite `additional_capacity`.
  const [limitEnabled, setLimitEnabled] = useState(false);

  const currentServiceType = drawer?.serviceType;

  const initialValues = React.useMemo(() => {
    if (!visible) return {};
    if (!currentServiceType) {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return {
        default_timezone: browserTimezone,
        process_type: SERVICE_PROCESSING_WORKFLOW_TYPE.INSTANT,
      };
    }
    const values = {...currentServiceType};
    if (!values.process_type)
      values.process_type = SERVICE_PROCESSING_WORKFLOW_TYPE.INSTANT;
    if (values.product_group)
      values.product_group_id = values.product_group.uuid;
    return values;
  }, [visible, currentServiceType]);

  const editMode = !isEmpty(currentServiceType);
  const [localExtraFields, setLocalExtraFields] = useState<ExtraFieldTypeModel[]>([]);

  const hasPendingChanges = editDirty && editMode;

  useAlertBeforeUnload(hasPendingChanges && visible)

  const handleOnNameBlur = (e) => {
    const {name, icon} = serviceTypeForm.getFieldsValue();
    if (name && !icon) {
      guessServiceTypeIconRequest({
        data: {name}
      }).then(response => {
        processAPIResponseNow(response, {
          onSuccess(data) {
            if (data.emoji) {
              serviceTypeForm.setFieldValue('icon', data.emoji)
              // No-ops while creating; the icon rides along with the create payload.
              updateServiceType({
                icon: data.emoji,
              })
            }
          },
          // Suggesting an icon is a convenience the user never asked for, so a
          // failure (e.g. the AI key is unset locally) stays silent instead of
          // toasting an error over a name they just typed.
          onError() {},
        }, 'data')
      })
    }
  }

  function toggle() {
    if (hasPendingChanges) {
      Modal.confirm({
        title: intl.formatMessage({
          id: 'pages.serviceTypes.confirmChanges.title',
          defaultMessage: 'Publish Changes',
        }),
        content: intl.formatMessage({
          id: 'pages.serviceTypes.confirmChanges.content',
          defaultMessage: 'You have edited some parts of this service type. Do you want to apply the template to all branches offering this service type?',
        }),
        okText: intl.formatMessage({
          id: 'pages.serviceTypes.confirmChanges.applyToAll',
          defaultMessage: 'Apply to All Branches',
        }),
        cancelText: intl.formatMessage({
          id: 'pages.serviceTypes.confirmChanges.justClose',
          defaultMessage: 'Just Close',
        }),
        onOk() {
          return new Promise((resolve, reject) => {
            updateAllServicesForServiceTypeRequest({
              params: {serviceTypeId: currentServiceType?.uuid},
            }).then(() => {
              dispatch({
                type: 'location/toggleServiceTypeDrawer',
              });
              resolve()
            })
          })

        },
        onCancel() {
          // Just close the drawer without applying to all branches
          dispatch({
            type: 'location/toggleServiceTypeDrawer',
          });
        },
      });
      return;
    }
    dispatch({
      type: 'location/toggleServiceTypeDrawer',
    });
  }

  const reset = () => {
    serviceTypeForm.resetFields();
  };

  const fetchServiceTypes = () => {
    dispatch({
      type: 'location/fetchServiceTypes',
    });
  };

  useEffect(() => {
    dispatch({
      type: 'location/getTimeZones',
    });
  }, []);

  useEffect(() => {
    if (!visible && initialized) {
      fetchServiceTypes()
      return;
    }

    setInitialized(true)
    setEditDirty(false)

    if (currentServiceType) {
      setLocalExtraFields(
        (currentServiceType.extra_fields || []).filter(
          (field) => !isEmpty(field.values),
        ),
      );
      const unlimited = !!currentServiceType.unlimited_booking;
      const capacity = currentServiceType.additional_capacity;
      const hasLimit = !unlimited && capacity != null && capacity > 0;
      setLimitEnabled(hasLimit);
      setOverbookingEnabled(unlimited || hasLimit);
    } else {
      setLocalExtraFields([]);
      setLimitEnabled(false);
      setOverbookingEnabled(false);
    }

  }, [visible, drawer]);

  const intl = useIntl();

  const handleFieldSelect = (field: ExtraFieldTypeModel) => {
    setLocalExtraFields((prev) => {
      if (prev.find((f) => f.uuid === field.uuid)) return prev;
      return [...prev, {...field, values: [], is_attached: false}];
    });
  };

  const handleFieldRemove = (fieldUuid) => {
    setLocalExtraFields(prev => prev?.filter((f) => f.uuid !== fieldUuid));
  };

  const handleFieldCreated = (field: ExtraFieldTypeModel) => {
    setLocalExtraFields((prev) => [...prev, {...field, values: [], is_attached: false}]);
  };

  const updateServiceType = (payload: Record<string, any>) => {
    const serviceTypeId = currentServiceType?.uuid;
    // Without an id the request hits `/serviceTypes/undefined` → "not found" toast.
    if (!serviceTypeId) return;
    editServiceTypeRequest({
      data: payload,
      params: {serviceTypeId},
    }).then((response) => {
      processAPIResponseNow(response, {
        showSuccessMessage: true,
        successEntity: INTL_ENTITY.SERVICE_TYPE,
      });
    });
  }
  // Debounced update function for instant editing
  const debouncedUpdate = debounce((payload) => {
    updateServiceType(payload)
  }, 500);


  const handleCreateServiceType = () => {
    serviceTypeForm.validateFields().then(values => {
      setLoading(true);
      const data = {
        ...values,
        ...buildOverbookingPayload(overbookingEnabled, limitEnabled, values.additional_capacity),
      };
      createServiceTypeRequest({data}).then(({response, data}) => {
        setLoading(false);
        fetchServiceTypes();
        message.success(getIntlTranslation(intl, {
          action: INTL_ACTION.CREATE_SUCCESS,
          entity: INTL_ENTITY.SERVICE_TYPE
        }));
        toggle();
        if (drawer?.onCreate) drawer.onCreate(data?.data);
      }).catch(() => {
        setLoading(false);
      });
    });
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === 'product_group_id' && value == -1) value = null;
    if (editMode && currentServiceType) {
      setEditDirty(true)
      debouncedUpdate({
        [fieldName]: value,
      });
    }
  };

  // Translates the UI-only overbooking controls into a backend-valid payload.
  // The backend rejects `additional_capacity` unless both `unlimited_booking` and
  // `ignore_agent_availability` are false, so whenever overbooking is active we always
  // force `ignore_agent_availability: false` (and flip its visible toggle elsewhere).
  // No limit (or a limit toggled on but left empty) means unlimited overbooking.
  const buildOverbookingPayload = (
    enabled: boolean,
    limited: boolean,
    capacity: number | null | undefined,
  ): Record<string, any> => {
    if (!enabled) {
      return {unlimited_booking: false, additional_capacity: null};
    }
    if (limited && capacity != null) {
      return {
        unlimited_booking: false,
        additional_capacity: capacity,
        ignore_agent_availability: false,
      };
    }
    return {unlimited_booking: true, additional_capacity: null, ignore_agent_availability: false};
  };

  const submitOverbooking = (enabled: boolean, limited: boolean, capacity: number | null | undefined) => {
    if (!editMode || !currentServiceType) return;
    setEditDirty(true);
    debouncedUpdate(buildOverbookingPayload(enabled, limited, capacity));
  };

  const handleOverbookingToggle = (enabled: boolean) => {
    setOverbookingEnabled(enabled);
    if (enabled) {
      // Keep an existing limit if one was already set, otherwise default to unlimited.
      const capacity = serviceTypeForm.getFieldValue('additional_capacity');
      const limited = limitEnabled && capacity != null;
      setLimitEnabled(limited);
      // Overbooking and "Ignore Agent Availability" cannot coexist — flip it off (visibly).
      serviceTypeForm.setFieldValue('ignore_agent_availability', false);
      submitOverbooking(true, limited, capacity);
    } else {
      submitOverbooking(false, limitEnabled, serviceTypeForm.getFieldValue('additional_capacity'));
    }
  };

  const handleLimitToggle = (checked: boolean) => {
    setLimitEnabled(checked);
    let capacity: number | null = null;
    if (checked) {
      // Default to 1 unless a valid limit is already set.
      const current = serviceTypeForm.getFieldValue('additional_capacity');
      capacity = current != null && current >= 1 ? current : 1;
    }
    serviceTypeForm.setFieldValue('additional_capacity', capacity);
    serviceTypeForm.setFieldValue('ignore_agent_availability', false);
    submitOverbooking(true, checked, capacity);
  };

  const handleIgnoreAgentChange = (checked: boolean) => {
    if (checked && overbookingEnabled) {
      // Last action wins: turning on "Ignore Agent Availability" disables overbooking.
      setOverbookingEnabled(false);
      setLimitEnabled(false);
      serviceTypeForm.setFieldValue('additional_capacity', null);
      if (editMode && currentServiceType) {
        setEditDirty(true);
        debouncedUpdate({
          ignore_agent_availability: true,
          unlimited_booking: false,
          additional_capacity: null,
        });
      }
      return;
    }
    handleFieldChange('ignore_agent_availability', checked);
  };

  const submitOnEnter = useSubmitOnEnter(
    editMode ? undefined : handleCreateServiceType,
    { enabled: !loading },
  );

  function getFooter() {
    return (
      <div className={styles.footerRight}>
        <Space>
          <Button
            className={'button-secondary button-bordered'}
            size={'large'}
            onClick={toggle}
          >
            <F id={'common.actions.close'} defaultMessage={'Close'}/>
          </Button>
          {!editMode && (
            <CheckAccess access={[PERMISSIONS.ServiceType.CREATE, PERMISSIONS.BUSINESS_PARTNER]}>
              <Button
                type={'primary'}
                size={'large'}
                loading={loading}
                onClick={handleCreateServiceType}
              >
                <F id={'common.actions.create'} defaultMessage={'Create'}/>
              </Button>
            </CheckAccess>
          )}
        </Space>
      </div>
    );
  }

  return (
    <Drawer
      width={650}
      afterOpenChange={(open) => !open && reset()}
      footer={getFooter()}
      title={getIntlTranslation(intl, {
        entity: INTL_ENTITY.SERVICE_TYPE,
        action: editMode ? INTL_ACTION.EDIT : INTL_ACTION.CREATE,
      })}
      open={visible}
      destroyOnHidden
      onOpenChange={toggle}
      rootClassName={styles.serviceTypeDrawerRoot}
    >
      <ProForm
        {...submitOnEnter}
        form={serviceTypeForm}
        initialValues={initialValues}
        submitter={false}
        layout="horizontal"
        labelCol={{span: 12}}
        labelAlign={'left'}
        colon={false}
        onValuesChange={(changedValues) => {
          if (editMode) {
            Object.keys(changedValues).forEach((fieldName) => {
              // These are handled onBlur
              if (['appointments_buffer', 'appointments_duration', 'timeslot_increment', 'appointments_from', 'appointments_until'].includes(fieldName)) return
              // Handled by their own onChange so the full derived payload is sent together.
              if (fieldName === 'ignore_agent_availability') return
              if (fieldName === 'additional_capacity') {
                // Typing a cap means a finite limit.
                setLimitEnabled(true);
                serviceTypeForm.setFieldValue('ignore_agent_availability', false);
                submitOverbooking(true, true, changedValues.additional_capacity);
                return
              }
              handleFieldChange(fieldName, changedValues[fieldName]);
            });
          }
        }}
      >
        <ProFormText
          name="name"
          className="form-item-mini-margin"
          label={intl.formatMessage({
            id: 'pages.location.service.name',
            defaultMessage: 'Service Name',
          })}
          fieldProps={{
            onBlur: handleOnNameBlur,
          }}
          tooltip={intl.formatMessage({
            id: 'pages.location.service.nameTooltip',
            defaultMessage:
              'This name is only used for external booking and customer interactions.',
          })}
          rules={[{required: true}]}
        />
        <Flex justify={'space-between'}>
          <EmojiSelectorFormItem
            className={`${styles.serviceTypeEmojiFormContainer} form-item-mini-margin`}
            name="icon"
            label={<F id="pages.serviceTypes.icon" defaultMessage="Icon"/>}
          />
          {editMode && currentServiceType && (
            <ProFormItem
              className={`${styles.serviceTypeThumbnailUpload} form-item-mini-margin`}
              label={<F id="common.thumbnail" defaultMessage="Thumbnail"/>}
            >
              <ThumbnailUpload
                currentThumbnail={currentServiceType.thumbnail}
                uploadUrl={URLParamBuilder(
                  Paths.UPLOAD_THUMBNAIL_FOR_SERVICE_TYPE,
                  {
                    params: {serviceTypeId: currentServiceType.uuid},
                    root: `${getRootUrl()}${API_PATH_PREFIX}`,
                  },
                )}
                onDelete={() =>
                  deleteServiceTypeThumbnailRequest({
                    params: {serviceTypeId: currentServiceType.uuid},
                  })
                }
                onUploadSuccess={fetchServiceTypes}
                onDeleteSuccess={fetchServiceTypes}
              />
            </ProFormItem>
          )}
        </Flex>
        <ProFormText
          name="name_template"
          className="form-item-mini-margin"
          initialValue={'{{service_name}} - {{branch_name}}'}
          tooltip={intl.formatMessage({
            id: 'pages.serviceTypes.nameTemplateTooltip',
            defaultMessage:
              'The naming template will be used for all services of this type.',
          })}
          label={
            <F
              id="pages.serviceTypes.nameTemplate"
              defaultMessage="Name Template"
            />
          }
        />
        <ProFormText
          name="slug"
          className="form-item-mini-margin"
          readonly={editMode}
          label={'Slug'}
          render={(dom, entity) => {
            return (
              <Typography.Text code copyable={{text: entity.value}}>
                {entity.value}
              </Typography.Text>
            );
          }}
        />

        <ProFormItem
          name="product_group_id"
          getValueFromEvent={(valueOrOption) => {
            return valueOrOption || -1;
          }}
          label={
            <F
              id="entity.product_group.singular"
              defaultMessage="Product Group"
            />
          }
        >
          <ProductGroupSelect
            filterMode={'select'}
            hasCreate
            variant="underlined"
          />
        </ProFormItem>
        <ProForm.Item
          name="default_timezone"
          label={
            <F
              id="pages.serviceTypes.defaultTimezone"
              defaultMessage="Default Timezone"
            />
          }
        >
          <Select
            options={timezones.map((tz: string) => ({
              label: tz,
              value: tz,
            }))}
            placeholder={intl.formatMessage({
              id: 'pages.serviceTypes.defaultTimezone',
              defaultMessage: 'Default Timezone',
            })}
            showSearch
            variant={'underlined'}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => handleFieldChange('default_timezone', value)}
          />
        </ProForm.Item>

        <ServiceBookingSettings
          form={serviceTypeForm}
          isServiceType
          onPatch={(values) => {
            if (editMode) {
              Object.keys(values).forEach((fieldName) => {
                handleFieldChange(fieldName, values[fieldName]);
              });
            }
          }}
        />

        <DrawerSection
          collapse
          title={<F id="common.advanced" defaultMessage="Advanced"/>}
        >
          <ProForm.Item
            name="process_type"
            className="form-item-mini-margin"
            label={
              <F
                id="pages.serviceTypes.processType"
                defaultMessage="Process Type"
              />
            }
          >
            <Segmented
              options={[
                {
                  label: intl.formatMessage({
                    id: 'pages.serviceTypes.processType.instant',
                    defaultMessage: 'Instant',
                  }),
                  value: SERVICE_PROCESSING_WORKFLOW_TYPE.INSTANT,
                },
                {
                  label: intl.formatMessage({
                    id: 'pages.serviceTypes.processType.confirmDecline',
                    defaultMessage: 'Confirm/Decline',
                  }),
                  value: SERVICE_PROCESSING_WORKFLOW_TYPE.CONFIRM_DECLINE,
                },
              ]}
              onChange={(value) => handleFieldChange('process_type', value)}
            />
          </ProForm.Item>
          <ProForm.Item
            name="max_req_in_future_per_customer"
            label={intl.formatMessage({
              id: 'pages.serviceTypes.meetingLimit',
              defaultMessage: 'Max. Bookings per Customer',
            })}
            tooltip={intl.formatMessage({
              id: 'pages.serviceTypes.meetingLimit.tooltip',
              defaultMessage:
                'Limits the number of upcoming appointments a customer can book on a single project at the same time',
            })}
          >
            <InputNumber
              variant={'underlined'}
              min={0}
            />
          </ProForm.Item>
          <ProForm.Item className="form-item-mini-margin">
            <ToggleCard
              title={
                <F
                  id="pages.serviceTypes.overbooking"
                  defaultMessage="Overbooking"
                />
              }
              description={
                <F
                  id="pages.serviceTypes.overbooking.description"
                  defaultMessage="Allow more appointments to be booked than the available capacity for this service type"
                />
              }
              checked={overbookingEnabled}
              onChange={handleOverbookingToggle}
            >
              {overbookingEnabled && (
                <ProForm.Item
                  className={styles.overbookingLimitRow}
                  label={intl.formatMessage({
                    id: 'pages.serviceTypes.overbookingLimit',
                    defaultMessage: 'Overbooking Limit',
                  })}
                  tooltip={intl.formatMessage({
                    id: 'pages.serviceTypes.overbookingLimit.tooltip',
                    defaultMessage: 'Without setting a limit, overbooking is unlimited.',
                  })}
                >
                  <Flex align="center" gap={12}>
                    <Switch size="small" checked={limitEnabled} onChange={handleLimitToggle} />
                    {limitEnabled && (
                      <ProForm.Item name="additional_capacity" noStyle>
                        <InputNumber variant="underlined" min={1} autoFocus />
                      </ProForm.Item>
                    )}
                  </Flex>
                </ProForm.Item>
              )}
            </ToggleCard>
          </ProForm.Item>

          <ProForm.Item
            name="ignore_date_on_booking"
            valuePropName="checked"
            className="form-item-mini-margin"
          >
            <ToggleCard
              title={
                <F
                  id="pages.serviceTypes.ignoreDateOnBooking"
                  defaultMessage="Ignore Date on Booking"
                />
              }
              description={
                <F
                  id="pages.serviceTypes.ignoreDateOnBookingTooltip"
                  defaultMessage="Only when 3rd party booking systems are used. Will force the meeting creation on random agent. No effect on availability. "
                />
              }
              onChange={(checked) =>
                handleFieldChange('ignore_date_on_booking', checked)
              }
            />
          </ProForm.Item>
          <ProForm.Item
            name="ignore_agent_availability"
            valuePropName="checked"
            className="form-item-mini-margin"
          >
            <ToggleCard
              title={
                <F
                  id="pages.serviceTypes.ignoreAgentAvailability"
                  defaultMessage="Ignore Agent Availability"
                />
              }
              description={
                <F
                  id="pages.serviceTypes.ignoreAgentsAvailabilityTooltip"
                  defaultMessage="Will create meeting without assigning it to an agent if no agent is available. Cannot be combined with unlimited booking."
                />
              }
              onChange={handleIgnoreAgentChange}
            />
          </ProForm.Item>

        </DrawerSection>
        <DrawerSection
          collapse
          title={
            <F
              id="entity.working_hours.plural"
              defaultMessage="Opening Hours"
            />
          }
        >
          <ProForm.Item name={'opening_hours'}>
            <UserOpenHours
              user={currentServiceType}
              dataExtractor={(serviceType) => serviceType?.opening_hours}
              toSlotsTransformer={transformServiceTypeOpenHoursToSlots}
              fromSlotsTransformer={transformSlotsToServiceTypeOpenHours}
              dataFormatter={(serviceType, transformedData) => transformedData}
              supportMultipleEntries={true}
              onChange={(openingHours, rawData) => {
                const hasIncomplete = Array.isArray(rawData) && rawData.some(
                  (day) =>
                    day.enabled && day.slots.some((s) => !s.start || !s.end),
                );
                if (hasIncomplete) return;

                serviceTypeForm.setFieldValue('opening_hours', openingHours);

                if (editMode && currentServiceType) {
                  handleFieldChange('opening_hours', openingHours);
                }
              }}
            />
          </ProForm.Item>
        </DrawerSection>
        {editMode && (
          <DrawerSection
            collapse
            title={
              <F
                id={'entity.extra_field.plural'}
                defaultMessage={'Extra Fields'}
              />
            }
          >
            <DynamicExtraFieldsForm
              fields={localExtraFields}
              removable
              labelWidth={250}
              onAfterRemove={handleFieldRemove}
              serviceTypeId={currentServiceType.uuid}
            />
            <ExtraFieldSelector
              relatedTo="serviceType"
              selectedFieldIds={localExtraFields.map((f) => f.uuid)}
              onFieldSelect={handleFieldSelect}
              onFieldCreated={handleFieldCreated}
            />
          </DrawerSection>
        )}
      </ProForm>
      {editMode && <CopyableId id={currentServiceType?.uuid}/>}
    </Drawer>
  );
};

export default ServiceTypesDrawer;
